import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LocalModel } from "../models/LocalModel";
import { fetchGetLocales } from "../services/reporteService";
import { useUserContext } from "./UserContext";

type LocalesContextType = {
  locales: LocalModel[];
  loading: boolean;
  refreshLocales: () => Promise<void>;
};

const LOCALES_STORAGE_KEY = "genfe_locales";

const LocalesContext = createContext<LocalesContextType | undefined>(undefined);

export const useLocales = (): LocalesContextType => {
  const context = useContext(LocalesContext);
  if (!context) {
    throw new Error("useLocales must be used within a LocalesProvider");
  }
  return context;
};

const readCachedLocales = (): LocalModel[] => {
  const raw = sessionStorage.getItem(LOCALES_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    sessionStorage.removeItem(LOCALES_STORAGE_KEY);
    return [];
  }
};

export const LocalesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getUser } = useUserContext();
  const user = getUser();
  const [locales, setLocales] = useState<LocalModel[]>(readCachedLocales);
  const [loading, setLoading] = useState(false);
  const loadedTokenRef = useRef<string | undefined>(undefined);

  const loadLocales = useCallback(async (forceRefresh: boolean) => {
    const currentUser = getUser();
    if (!currentUser) {
      setLocales([]);
      sessionStorage.removeItem(LOCALES_STORAGE_KEY);
      loadedTokenRef.current = undefined;
      return;
    }

    if (!forceRefresh && loadedTokenRef.current === currentUser.token) {
      return;
    }

    setLoading(true);
    try {
      const nextLocales = await fetchGetLocales(currentUser);
      const localesDisponibles = nextLocales ?? [];
      setLocales(localesDisponibles);
      loadedTokenRef.current = currentUser.token;
      sessionStorage.setItem(LOCALES_STORAGE_KEY, JSON.stringify(localesDisponibles));
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  const refreshLocales = useCallback(async () => {
    await loadLocales(true);
  }, [loadLocales]);

  useEffect(() => {
    if (user?.token) {
      loadLocales(false).catch((error) => console.error("Error loading locales:", error));
    } else {
      setLocales([]);
      loadedTokenRef.current = undefined;
      sessionStorage.removeItem(LOCALES_STORAGE_KEY);
    }
  }, [user?.token, loadLocales]);

  const value = useMemo(() => ({ locales, loading, refreshLocales }), [locales, loading, refreshLocales]);

  return (
    <LocalesContext.Provider value={value}>
      {children}
    </LocalesContext.Provider>
  );
};
